import { Can } from "@dashboard/casl/taskAbility";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import { subject } from "@casl/ability";
import { closestCenter, DndContext, MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { faCircleInfo, faPencil, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    Widget,
    WidgetActions,
    WidgetContent,
    WidgetHeader,
    WidgetTitle,
} from "frontend-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShortcutModal } from "./ShortcutModal";
import { ShortcutTile } from "./ShortcutTile";
import { useDeleteShortcut, useReorderShortcuts, useShortcuts } from "./useShortcuts";
import type { TaskShortcut } from "@dashboard/api/task";

export default function ShortcutsWidget() {
    const { t } = useTranslation();
    const { instanceId } = useHseInstance();
    const { data: shortcuts = [], isError } = useShortcuts(instanceId);
    const deleteMutation = useDeleteShortcut();
    const reorderMutation = useReorderShortcuts(instanceId);

    const [editMode, setEditMode] = useState(false);
    const [shortcutToEdit, setShortcutToEdit] = useState<TaskShortcut | null>(null);
    const [shortcutToDelete, setShortcutToDelete] = useState<TaskShortcut | null>(null);
    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));

    const handleDelete = async (id: number) => {
        await deleteMutation.mutateAsync(id);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = shortcuts.findIndex((s) => s.id === active.id);
        const newIndex = shortcuts.findIndex((s) => s.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(shortcuts, oldIndex, newIndex).map((s, i) => ({
            ...s,
            order: i,
        }));

        reorderMutation.mutate(reordered);
    };

    if (isError) {
        return (
            <Widget>
                <WidgetHeader>
                    <WidgetTitle>{t("dashboard.shortcuts.title")}</WidgetTitle>
                </WidgetHeader>
                <WidgetContent className="items-center justify-center text-sm text-chameleon-pink">
                    {t("common.errorLoadingData")}
                </WidgetContent>
            </Widget>
        );
    }

    return (
        <Widget>
            <WidgetHeader>
                <WidgetTitle>{t("dashboard.shortcuts.title")}</WidgetTitle>
                <span className="inline-flex h-5 min-w-6 items-center justify-center rounded-full bg-compendia-greige-40 px-2 text-[11px] font-semibold text-compendia-foreground">
                    {shortcuts.length}
                </span>
                <Can I="create" this={subject("TaskShortcut", { instanceId })}>
                    <WidgetActions>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => setEditMode((v) => !v)}
                            aria-label={t("common.edit")}
                        >
                            <FontAwesomeIcon icon={faPencil} />
                        </Button>
                        <ShortcutModal
                            mode="create"
                            trigger={
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label={t("common.add")}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </Button>
                            }
                        />
                    </WidgetActions>
                </Can>
            </WidgetHeader>
            <WidgetContent className="gap-3 overflow-y-auto">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={shortcuts.map((s) => s.id)}
                        strategy={rectSortingStrategy}
                    >
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2">
                            {shortcuts.map((shortcut) => (
                                <ShortcutTile
                                    key={shortcut.id}
                                    shortcut={shortcut}
                                    editMode={editMode}
                                    onEditClick={setShortcutToEdit}
                                    onDeleteClick={setShortcutToDelete}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>

                {shortcuts.length === 0 && !editMode && (
                    <p className="py-4 text-center text-sm text-compendia-muted">
                        {t("dashboard.shortcuts.empty")}
                    </p>
                )}

                {editMode && (
                    <div className="hidden items-center gap-2 rounded-md bg-compendia-greige-40 px-3 py-2 text-xs text-compendia-foreground pointer-fine:flex">
                        <FontAwesomeIcon icon={faCircleInfo} className="shrink-0" />
                        {t("dashboard.shortcuts.editHint")}
                    </div>
                )}
            </WidgetContent>
            {shortcutToEdit && (
                <ShortcutModal
                    mode="edit"
                    shortcut={shortcutToEdit}
                    open={shortcutToEdit !== null}
                    onOpenChange={(open) => {
                        if (!open) setShortcutToEdit(null);
                    }}
                />
            )}
            <ConfirmDeleteDialog
                open={shortcutToDelete !== null}
                onOpenChange={(open) => {
                    if (!open) setShortcutToDelete(null);
                }}
                onConfirm={async () => {
                    if (shortcutToDelete) await handleDelete(shortcutToDelete.id);
                }}
                title={t("dashboard.shortcuts.confirmDeleteTitle")}
                description={t("dashboard.shortcuts.confirmDeleteDescription", {
                    title: shortcutToDelete?.title ?? "",
                })}
            />
        </Widget>
    );
}
